const PENDING_VERIFICATION_ERROR_CODE = "AUTH_ACCOUNT_PENDING_VERIFICATION";

const AUTHENTICATION_ERROR_CODES = new Set([
  "AUTH_INVALID_CREDENTIALS",
  "AUTHENTICATION_ERROR",
  "AUTH_LOGIN_FAILED",
  "INVALID_CREDENTIALS",
]);

const ACCOUNT_ACCESS_ERROR_CODES = new Set([
  "AUTH_ACCOUNT_DISABLED",
  "AUTH_ACCOUNT_INACTIVE",
  "AUTH_ACCOUNT_LOCKED",
  "AUTH_ACCOUNT_SUSPENDED",
]);

const SECURITY_CHECK_ERROR_CODES = new Set([
  "AUTH_PUBLIC_CHALLENGE_REQUIRED",
  "CAPTCHA_REQUIRED",
  "SECURITY_CHALLENGE_REQUIRED",
]);

const RATE_LIMIT_ERROR_CODES = new Set([
  "AUTH_RATE_LIMITED",
  "RATE_LIMITED",
  "TOO_MANY_REQUESTS",
]);

const SIGN_IN_INVALID_MESSAGE =
  "We couldn't sign you in. Check your email and password, then try again.";
const SIGN_IN_ACCOUNT_ACCESS_MESSAGE =
  "We couldn't sign you in. Contact support if you think this is a mistake.";
const SIGN_IN_PENDING_VERIFICATION_MESSAGE =
  "Check your email for a verification link before signing in.";
const SIGN_IN_SECURITY_CHECK_MESSAGE =
  "Please complete the security check, then try signing in again.";
const SIGN_IN_RATE_LIMIT_MESSAGE =
  "Too many sign-in attempts. Please wait a few minutes and try again.";
const SIGN_IN_SERVICE_MESSAGE =
  "We couldn't reach the sign-in service. Please try again in a few minutes.";
const SIGN_IN_VALIDATION_MESSAGE = "Enter your email and password to sign in.";

const TWO_FACTOR_INVALID_MESSAGE =
  "The code is incorrect or expired. Try again.";
const TWO_FACTOR_SERVICE_MESSAGE =
  "We couldn't verify the code. Please try again in a few minutes.";

interface AuthErrorLike {
  code?: string;
  status?: number;
}

const getAuthErrorLike = (error: unknown): AuthErrorLike => {
  if (error === null || typeof error !== "object") {
    return {};
  }

  const code = Reflect.get(error, "code");
  const status = Reflect.get(error, "status");

  return {
    code: typeof code === "string" ? code : undefined,
    status: typeof status === "number" ? status : undefined,
  };
};

export const isPendingVerificationSignInError = (error: unknown): boolean => {
  return getAuthErrorLike(error).code === PENDING_VERIFICATION_ERROR_CODE;
};

export const getSignInErrorMessage = (error: unknown): string => {
  const { code, status } = getAuthErrorLike(error);

  if (code === PENDING_VERIFICATION_ERROR_CODE) {
    return SIGN_IN_PENDING_VERIFICATION_MESSAGE;
  }

  if (code && ACCOUNT_ACCESS_ERROR_CODES.has(code)) {
    return SIGN_IN_ACCOUNT_ACCESS_MESSAGE;
  }

  if (code && SECURITY_CHECK_ERROR_CODES.has(code)) {
    return SIGN_IN_SECURITY_CHECK_MESSAGE;
  }

  if (code && RATE_LIMIT_ERROR_CODES.has(code)) {
    return SIGN_IN_RATE_LIMIT_MESSAGE;
  }

  if (code === "VALIDATION_ERROR") {
    return SIGN_IN_VALIDATION_MESSAGE;
  }

  if (code && AUTHENTICATION_ERROR_CODES.has(code)) {
    return SIGN_IN_INVALID_MESSAGE;
  }

  if (status === 400) {
    return SIGN_IN_VALIDATION_MESSAGE;
  }

  if (status === 401) {
    return SIGN_IN_INVALID_MESSAGE;
  }

  if (status === 403) {
    return SIGN_IN_ACCOUNT_ACCESS_MESSAGE;
  }

  if (status === 429) {
    return SIGN_IN_RATE_LIMIT_MESSAGE;
  }

  return SIGN_IN_SERVICE_MESSAGE;
};

export const getTwoFactorErrorMessage = (error: unknown): string => {
  const { code, status } = getAuthErrorLike(error);

  if (code && RATE_LIMIT_ERROR_CODES.has(code)) {
    return SIGN_IN_RATE_LIMIT_MESSAGE;
  }

  if (status === 400 || status === 401 || status === 403) {
    return TWO_FACTOR_INVALID_MESSAGE;
  }

  return TWO_FACTOR_SERVICE_MESSAGE;
};
